/**
 * KIR-4836 — analyst-facing copy for the `tool-call-scan` alert panel, kept in
 * one module so the wording can be reviewed without reading JSX.
 *
 * Every string here has to match what the pipeline actually measured, which is
 * why three of them deviate from the ticket's tooltip mockup (PR #2685 review):
 *
 * - The malicious-score tooltip and the verdict pill no longer say "above" a
 *   threshold "set by the policy sensitivity". The backend flags on
 *   `mal_score >= TOOL_CALL_ANALYSIS_MAL_SCORE_THRESHOLD`, a fixed constant in
 *   `tool-call-analysis-adapter.ts` that only a backend release moves, and the
 *   comparison is inclusive — a score sitting exactly on the cutoff is flagged.
 * - The session chips describe latch bits that `snapshotForPreToolUse`
 *   (`extensions/agent/pkg/hooks/toolcallscan_session.go`) reads BEFORE
 *   applying this call, so `Yes` means an earlier call in the session set the
 *   bit — never this one. Each tooltip now names the call that sets it.
 */

/** Tooltips on the facts-strip cells, passed to the strip only for this type. */
export const TOOL_CALL_FACT_TOOLTIPS = {
  outcome:
    'What Kirin did about this call. Prevented means it was blocked before it ran; Detected means it was reported only.',
  riskScore:
    "Kirin's 0–100 score for this alert group. Higher means more urgent.",
} as const;

/** Tooltips inside the "Why Kirin AI flagged this" card. */
export const TOOL_CALL_TOOLTIPS = {
  maliciousScore:
    'Malicious score is the model score from 0 to 1. The call is flagged once the score reaches the threshold — a fixed cutoff in the Kirin backend, not a policy sensitivity setting.',
  categorySignals:
    "Per-category scores from the real-time model for this tool call. The highest-scoring threat category sets the malicious score; the list also carries the model's benign class, which never does.",
  sensitivePath:
    "Set when an earlier Read, Write or Edit in this session opened a path matching Kirin's credential-file patterns. It describes the session before this call, not this call.",
  egressPrior:
    'Set when an earlier call in this session ran a network command such as curl, wget or ssh. It describes the session before this call, not this call.',
  unknownPkg:
    'Set when an earlier Bash command in this session contained a package-install marker such as "pip install" or "npm install". It is a marker match, not a check of whether Kirin knows the package.',
  sincePrompt:
    "How many tool calls the agent made since the user's last prompt. A high number can mean the agent is acting on its own. Counted before this call, so it reads 0 on the first call after the prompt.",
} as const;

/**
 * Tooltip for one category-signal row. The label taxonomy is not enumerated
 * anywhere on the contract (see `tool-call-detail.helpers.ts`), so there is no
 * per-label copy to look up — the row explains what the number is instead.
 */
export function categorySignalTooltip(label: string): string {
  return `${label}: the model's score from 0 to 1 for this category on this call. Bars are shaded against the malicious-score threshold as a rough guide; only the malicious score decides the verdict.`;
}

/** Headings and inline labels. */
export const TOOL_CALL_LABELS = {
  toolCallCard: 'Tool call',
  verdictCard: 'Why Kirin AI flagged this',
  maliciousScore: 'Malicious score vs threshold',
  categorySignals: 'Category signals',
  additionalSignals: 'Additional signals',
  additionalSignalsHint: '(gray when No, red when Yes)',
  showDetails: 'Show detection details',
  hideDetails: 'Hide detection details',
  flagged: 'Flagged',
  allowed: 'Allowed',
} as const;

/** The pill under the score bar, stating the threshold comparison in words. */
export const TOOL_CALL_VERDICT_PILL = {
  flagged: 'At or above the threshold, so the call was flagged',
  allowed: 'Below the threshold, so the call was not flagged',
} as const;
